import { Component } from '@theme/component';

/**
 * DAVIDsTEA Matcha testimonials rail.
 *
 * Same shape as the recipes rail: an overflow container with snap points, so a finger,
 * a trackpad and the keyboard all work before this file loads. The script only adds the
 * two arrows that sit over the outer cards.
 *
 * The arrows stay hidden until this runs, and hide again whenever the rail has nothing to
 * scroll: `data-overflow`, measured on every scroll and every resize. Three testimonials fit
 * the 788px rail exactly and the pair never comes; a fourth overruns it by 198px at every
 * desktop width, and there it does.
 *
 * The step is measured between the first two items rather than hard-coded. The cards
 * overlap by design, so the step is narrower than a card and moves with the stylesheet.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
export class DtMatchaTestimonials extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#sync);
    this.#observer.observe(rail);

    this.#sync();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /**
   * One card plus one gap, and the gap here is negative. Falls back to the card width
   * alone when a single card is left, and to zero when the rail is empty.
   */
  get #step() {
    const items = this.refs.rail.querySelectorAll('.dt-matcha-testimonials__item');
    const first = items[0];
    if (!(first instanceof HTMLElement)) return 0;
    const second = items[1];
    if (second instanceof HTMLElement) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    }
    return first.getBoundingClientRect().width;
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} direction */
  #move(direction) {
    const step = this.#step;
    if (!step) return;
    this.refs.rail.scrollBy({ left: step * direction, behavior: this.#behavior });
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);
  #onScroll = () => this.#sync();

  /**
   * A single pixel of slack on each end: browsers land on fractional scroll offsets, and
   * an exact comparison leaves an arrow live at a stop it cannot move past.
   */
  #sync = () => {
    const { rail, prev, next } = this.refs;
    const max = rail.scrollWidth - rail.clientWidth;
    const overflows = max > 1;

    this.toggleAttribute('data-overflow', overflows);
    prev.disabled = !overflows || rail.scrollLeft <= 1;
    next.disabled = !overflows || rail.scrollLeft >= max - 1;
  };
}

if (!customElements.get('dt-matcha-testimonials-component')) {
  customElements.define('dt-matcha-testimonials-component', DtMatchaTestimonials);
}
